/**
 * Keeps the Add-to-cart button price in sync with the selected
 * "Buy More, Save More" tier from the Rapi Bundles app.
 *
 * Why this exists
 * ---------------
 * The theme's <product-buy-price> element (assets/theme.js) only updates when a
 * `bundle:change` CustomEvent is dispatched on the product form. The sole
 * dispatcher is ProductBundleDetails, the theme's OWN bundle component, which
 * reads `input[name="bundles"]`. The Rapi app is an independent widget using
 * `input[name="rapi-bundles-deal-<productId>"]` and never dispatches that event,
 * so the button kept showing the Liquid-rendered single-unit price.
 *
 * Dispatching `bundle:change` would not work either: its handler computes
 * `basePrice + Σ(item.price × item.quantity)`, an additive cross-sell model.
 * A volume discount REPLACES the total, so the additive math gives the
 * pre-discount figure (e.g. €74,97 instead of €52,48).
 *
 * Approach: read the price the app itself renders in the selected tier
 * (`.rapi-bundles__bar-price`) and mirror that string into the button. The app
 * stays the single source of truth, so every discount type, market and currency
 * format it supports is handled without duplicating its pricing logic here.
 *
 * The app calls preventDefault() on tier clicks and sets `.checked`
 * programmatically, which fires no `change`/`input` event — so selection is
 * detected via MutationObserver rather than form events.
 */
(function () {
  'use strict';

  var RADIO_PREFIX = 'rapi-bundles-deal-';
  var BARS = '.rapi-bundles__bars';
  var BAR = '.rapi-bundles__bar';
  var BAR_PRICE = '.rapi-bundles__bar-price';

  var observedWidgets = new WeakSet();
  var timer = null;

  /** Product ids that currently have a bundle widget on the page. */
  function productIdsWithWidget() {
    var ids = [];
    var radios = document.querySelectorAll('input[name^="' + RADIO_PREFIX + '"]');

    for (var i = 0; i < radios.length; i++) {
      var id = radios[i].name.slice(RADIO_PREFIX.length);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }

    return ids;
  }

  /**
   * Mirror the selected tier's price into that product's buy button.
   * No selection, no price node, or no button price => leave the button alone,
   * so products without the app keep their normal Liquid-rendered price.
   */
  function syncProduct(productId) {
    var checked = document.querySelector(
      'input[name="' + RADIO_PREFIX + productId + '"]:checked'
    );
    if (!checked) return;

    var bar = checked.closest(BAR);
    var priceNode = bar && bar.querySelector(BAR_PRICE);
    var price = priceNode && priceNode.textContent.trim();
    if (!price) return;

    // buy-buttons.liquid renders #BuyButtonPrice-<section_id>-<product_id>
    var wrapper = document.querySelector(
      '[id^="BuyButtonPrice-"][id$="-' + productId + '"]'
    );
    var target = wrapper && wrapper.querySelector('product-buy-price');
    if (!target) return;

    if (target.textContent.trim() !== price) target.textContent = price;
  }

  function syncAll() {
    timer = null;
    productIdsWithWidget().forEach(syncProduct);
  }

  /**
   * Coalesce bursts of mutations into one update per tick.
   *
   * setTimeout rather than requestAnimationFrame: rAF is suspended while a tab
   * is hidden, so a product opened in a background tab would keep showing the
   * single-unit price until the customer focused it. This is a text swap, so
   * there is nothing to gain by aligning it to paint.
   */
  function schedule() {
    if (timer !== null) return;
    timer = setTimeout(syncAll, 0);
  }

  /** Watch each widget for the app re-rendering its tiers. */
  function bindWidgets() {
    var widgets = document.querySelectorAll(BARS);

    for (var i = 0; i < widgets.length; i++) {
      if (observedWidgets.has(widgets[i])) continue;
      observedWidgets.add(widgets[i]);
      new MutationObserver(schedule).observe(widgets[i], {
        subtree: true,
        childList: true,
        attributes: true
      });
      schedule();
    }
  }

  function start() {
    bindWidgets();

    // The app injects its widget asynchronously, and a variant change re-renders
    // the product section (replacing both the widget and the button). Watching
    // body/childList re-binds in both cases. attributes are deliberately not
    // observed here — too noisy on a product page.
    new MutationObserver(function () {
      bindWidgets();
      schedule();
    }).observe(document.body, { childList: true, subtree: true });

    // Belt and braces: the app updates its state asynchronously after a click,
    // so re-sync shortly afterwards in case no mutation lands on the widget.
    document.addEventListener(
      'click',
      function (event) {
        if (event.target.closest && event.target.closest(BAR)) {
          setTimeout(syncAll, 0);
          setTimeout(syncAll, 300);
        }
      },
      true
    );

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
